import * as Sentry from '@sentry/react';
import { buildVersion } from '@constants/buildVersion';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import { useEffect } from 'react';

const currentURL = window.location.href;
const urlObject = new URL(currentURL);
const { hostname } = urlObject;

const prodEnvironments = ['hubl.whitevision.nl', 'hubl.whitevision.com'];
const testEnvironments = ['hubl.pages.dev', 'dev.docbaseweb.nl', 'test.docbaseweb.nl', 'acc.docbaseweb.nl'];
const devEnvironments = ['localhost'];

enum SentryEnvironment {
    PROD = 'PROD',
    TEST = 'TEST',
    DEV = 'DEV',
    UNKNOWN = 'UNKNOWN',
}

const isTestEnvironment = (hostname: string): boolean => {
    return testEnvironments.includes(hostname) || /^[a-f0-9]+\.hubl\.pages\.dev$/.test(hostname);
};

const sentryEnvironment = prodEnvironments.includes(hostname)
    ? SentryEnvironment.PROD
    : isTestEnvironment(hostname)
      ? SentryEnvironment.TEST
      : devEnvironments.includes(hostname)
        ? SentryEnvironment.DEV
        : SentryEnvironment.UNKNOWN;

if (sentryEnvironment !== SentryEnvironment.DEV) {
    Sentry.init({
        dsn: 'https://a0ec653dfaaa3ea9ffbe0363214b237f@monitoring.hubl.online/2',
        environment: sentryEnvironment,
        release: buildVersion,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect: useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.browserProfilingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: false,
                blockAllMedia: false,
                networkDetailAllowUrls: [
                    window.location.origin,
                    /^https:\/\/hubl\.pages\.dev\/.*/,
                    /^https:\/\/.*\.hubl\.pages\.dev\/.*/,
                    /^https:\/\/whitevision\.com\/.*/,
                    /^https:\/\/whitevision\.nl\/.*/,
                    /^https:\/\/.*\.workflowindecloud\.nl\/.*/,
                    /^https:\/\/.*\.docbaseweb\.nl\/.*/,
                    /^https:\/\/localhost:44325\/.*/,
                    /^https:\/\/monitoring\.hubl\.online\/.*/,
                    /^https:\/\/analytics\.hubl\.online\/.*/,
                ],
                networkRequestHeaders: ['Cache-Control'],
            }),
            Sentry.captureConsoleIntegration({
                levels: ['error'],
            }),
        ],
        tracesSampleRate: 0.25,
        tracePropagationTargets: [
            'localhost',
            /^.*\/api/,
            /^https:\/\/.*\.hubl\.pages\.dev\/.*/,
            /^https:\/\/hubl\.pages\.dev\/.*/,
            /^https:\/\/devweb.docbaseweb\.nl\/api/,
            /^https:\/\/testweb.docbaseweb\.nl\/api/,
            /^.*\.workflowindecloud\.nl\/api/,
        ],
        profilesSampleRate: 0.25,
    });
}
